import {
  getExtensionFromFilename,
  parseFileName,
} from "@/utils/getExtensionFromMime.ts";

import type { FileVersionMetadata } from "../types/fileVersionMetadata";

/**
 * Finds the existing request attachment a newly-added file supersedes, matching
 * by base name + extension (the same rule the my-files upload modal uses). This
 * is what lets the "updating previous version" tooltip appear automatically and
 * ensures a version is linked even when the user never opens the combobox.
 */
export function findPreviousVersionId(
  fileName: string,
  myFiles: { id: number; name: string }[] | undefined,
): null | number {
  if (!myFiles?.length) return null;

  let target: { baseName: string; ext: string };

  try {
    target = parseFileName(fileName);
  } catch {
    return null;
  }

  const match = myFiles.find((existing) => {
    try {
      const parsed = parseFileName(existing.name);

      return parsed.baseName === target.baseName && parsed.ext === target.ext;
    } catch {
      return false;
    }
  });

  return match?.id ?? null;
}

/** Returns a `File` with the user-edited base name and the original extension. */
export function buildNamedUploadFile(
  file: File,
  displayBaseName: string,
): File {
  const extension = getExtensionFromFilename(file.name);
  const normalizedBaseName = displayBaseName.trim();

  if (!normalizedBaseName) return file;

  const nextName = extension
    ? `${normalizedBaseName}.${extension}`
    : normalizedBaseName;

  if (nextName === file.name) return file;

  return new File([file], nextName, {
    lastModified: file.lastModified,
    type: file.type,
  });
}

export function createInitialFileMetadata(
  file: File,
  myFiles?: { id: number; name: string }[],
): FileVersionMetadata {
  const previousVersionId = findPreviousVersionId(file.name, myFiles);

  try {
    const { baseName } = parseFileName(file.name);

    return {
      displayBaseName: baseName,
      previousVersionId,
    };
  } catch {
    const extension = getExtensionFromFilename(file.name);
    const baseName = extension
      ? file.name.slice(0, -(extension.length + 1))
      : file.name;

    return {
      displayBaseName: baseName,
      previousVersionId,
    };
  }
}

export function createMetadataFromAttachmentName(
  name: string,
): FileVersionMetadata {
  try {
    const { baseName } = parseFileName(name);

    return {
      displayBaseName: baseName,
      previousVersionId: null,
    };
  } catch {
    return {
      displayBaseName: name,
      previousVersionId: null,
    };
  }
}

/** Accept only the same image format as the attachment being replaced. */
export function isSameImageFormat(file: File, originalMime: string): boolean {
  if (file.type === originalMime) return true;

  const jpegTypes = new Set(["image/jpeg", "image/jpg"]);
  const pngTypes = new Set(["image/png"]);

  if (jpegTypes.has(originalMime) && jpegTypes.has(file.type)) {
    return true;
  }

  return pngTypes.has(originalMime) && pngTypes.has(file.type);
}

export function acceptAttributeForMime(mime: string): string {
  if (mime === "image/jpeg") return "image/jpeg";
  if (mime === "image/png") return "image/png";

  return "image/jpeg,image/png";
}

export function formatLabelForMime(mime: string): string {
  if (mime === "image/jpeg") return "JPG";

  return "PNG";
}
